/**
 * Service intents: who should take a job, expiring certificates, how
 * customers rated us, remote support, which sessions became knowledge
 * articles, and which contracts are up for renewal.
 */

import { MINES } from '../../../data/core.ts';
import type { CustomerSatisfactionResponse, RosterEntry } from '../../../schema/person.ts';
import { unpackTable } from '../../columnar.ts';
import { dispatchProposals, groupNum, renewals, techStats } from '../../oem-engine.ts';
import { loadOemDomain } from '../../useOemData.ts';
import { firstName, route } from '../answer-kit.ts';
import { loadWorkOrders } from '../tools.ts';
import { cues, type IntentDef } from './types.ts';

// ---------------------------------------------------------------------------
// dispatch — demo L4053
// ---------------------------------------------------------------------------

export const dispatch: IntentDef = {
  id: 'dispatch',
  family: 'service',
  priority: 99,
  examples: ['Which technician should take the S-09 rotary head job and why?'],
  score: cues({ any: [/which technician|who should take|dispatch|\bassign\b|who (can|should) (go|attend|do)/], base: 5 }),
  async answer({ kit, E }) {
    const mid = E.machine;
    const [techJobs, workOrders, people, costs] = await Promise.all([
      loadOemDomain('service'),
      loadWorkOrders(),
      loadOemDomain('people'),
      loadOemDomain('costs'),
    ]);
    const dctx = {
      techJobs: techJobs.tapp_jobs,
      workOrders,
      techs: people.tapp_techs,
      roster: unpackTable<RosterEntry>(people.roster),
      css: unpackTable<CustomerSatisfactionResponse>(people.css),
      timesheets: costs.timesheets,
      training: techJobs.lms,
    };
    kit.ran('dispatch_proposals', { machine_id: mid });
    const props = dispatchProposals(dctx);
    const pick = mid ? props.find((p) => p.machine === mid) : props[0];
    if (!pick) {
      return kit.done(
        `There is no open job on ${mid} to dispatch — the proposals cover ${props
          .map((p) => p.machine)
          .join(', ')}. Ask about one of those.`,
        ['dispatch proposals'],
      );
    }
    kit.ran('technician_stats', { technician: pick.best });
    const st = techStats(pick.best, dctx);
    const certs = st.expiring.length
      ? `, and **${st.expiring.length} certificate${st.expiring.length > 1 ? 's' : ''} to renew** (${st.expiring
          .map((x) => firstName(x.course))
          .join(', ')})`
      : '';
    const markdown =
      `**${pick.best}** for ${pick.ref} on ${pick.machine} (${pick.site}) — ${pick.why}.` +
      `${pick.alt ? ` Alternative: ${pick.alt}.` : ''}\n\n` +
      `${pick.best} has ${st.done} completed jobs, fix-first-time ${st.fft == null ? 'n/a' : `${st.fft}%`}, ` +
      `CSS ${st.cssAvg ? `${st.cssAvg.toFixed(1)}/5` : 'no responses'}${certs}. ` +
      `The proposal is a draft — assign it from the Technicians page.`;
    kit.focus({ machine: pick.machine });
    return kit.done(
      markdown,
      ['dispatch proposals', 'xreach_tech_status', 'training_records', 'css_responses'],
      { navigate: route('techs', pick.best) },
    );
  },
};

// ---------------------------------------------------------------------------
// training — demo L4056
// ---------------------------------------------------------------------------

export const training: IntentDef = {
  id: 'training',
  family: 'service',
  priority: 98,
  examples: ['Whose certificates expire in the next 45 days?'],
  score: cues({
    any: [/certificat|training|\blms\b|tickets? (expir|due)|competenc|whose .*expir|expir.*(cert|ticket)/],
    not: [/contract/],
    base: 5,
  }),
  async answer({ kit }) {
    kit.ran('training_expiry');
    const service = await loadOemDomain('service');
    const ex = service.lms
      .filter((r) => r.status !== 'Current')
      .slice()
      .sort((a, b) => a.expires.localeCompare(b.expires));
    const meridian = ex
      .filter((x) => x.org === 'Meridian Equipment')
      .map((x) => `${x.person} · ${firstName(x.course)}`)
      .join('; ');
    const markdown =
      `**${ex.length} training records** are expiring or expired across technicians, operators and supervisors. ` +
      `Soonest: ${ex
        .slice(0, 5)
        .map((x) => `${x.person} — ${firstName(x.course)} (${x.status.toLowerCase()} ${x.expires})`)
        .join('; ')}. ` +
      `Meridian technicians: ${meridian || 'none'}.`;
    return kit.done(markdown, ['training_records_2026-09-09.csv'], { navigate: route('techs') });
  },
};

// ---------------------------------------------------------------------------
// customer_satisfaction — demo L4057
// ---------------------------------------------------------------------------

export const customerSatisfaction: IntentDef = {
  id: 'customer_satisfaction',
  family: 'service',
  priority: 97,
  examples: ['How did customers rate us this month?'],
  score: cues({ any: [/customer(s)? rat|\bcss\b|satisfaction|how did customers|customer (feedback|survey)/], base: 5 }),
  async answer({ kit }) {
    kit.ran('customer_satisfaction', { group_by: 'site' });
    const people = await loadOemDomain('people');
    const rows = unpackTable<CustomerSatisfactionResponse>(people.css);
    const by: Record<string, number[]> = {};
    rows.forEach((r) => {
      (by[r.Site] = by[r.Site] ?? []).push(r.Overall);
    });
    const avg = (v: number[]) => (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1);
    const comments = rows
      .filter((r) => r.Comment)
      .slice(0, 3)
      .map((r) => `"${r.Comment}" (${r.Site})`)
      .join('; ');
    const markdown =
      `**${avg(rows.map((r) => r.Overall))}/5 overall** across ${rows.length} surveyed jobs this month ` +
      `(quality ${avg(rows.map((r) => r['Quality (1-5)']))}, responsiveness ${avg(
        rows.map((r) => r['Responsiveness (1-5)']),
      )}, reporting ${avg(rows.map((r) => r['Reporting (1-5)']))}).\n\n` +
      `| Site | Responses | Mean |\n|---|---|---|\n` +
      Object.entries(by)
        .map(([k, v]) => `| ${k} | ${v.length} | ${avg(v)} |`)
        .join('\n') +
      `\n\nLowest scores are for **reporting** — the report arriving a day after the job. ` +
      `Comments worth reading: ${comments}.`;
    return kit.done(markdown, ['css_responses_2026-09.xlsx'], { navigate: route('customers') });
  },
};

// ---------------------------------------------------------------------------
// remote_support — demo L4064
// ---------------------------------------------------------------------------

export const remoteSupport: IntentDef = {
  id: 'remote_support',
  family: 'service',
  priority: 90,
  examples: ['How much travel has remote support avoided?'],
  score: cues({ any: [/travel.*(avoided|saved)|remote support|xreach|remote sessions?/] }),
  async answer({ kit }) {
    kit.ran('remote_sessions');
    const service = await loadOemDomain('service');
    const S = service.xreach_sessions;
    const km = S.reduce((a, x) => a + x.travel_avoided_km, 0);
    const markdown =
      `Remote support has run **${S.length} sessions** in ten weeks with a ` +
      `**${Math.round((S.filter((x) => x.first_time_fix).length / S.length) * 100)}% first-time fix** rate and ` +
      `**${groupNum(km)} km of travel avoided** (≈ A$${Math.round((km * 1.9) / 1000)}k). ` +
      `${service.kb.length} sessions became knowledge articles — the latest KB-0143 from the L-33 connector call ` +
      `at Mount Read this morning.`;
    return kit.done(markdown, ['xreach_sessions.json', 'knowledge articles'], {
      navigate: route('remote'),
    });
  },
};

// ---------------------------------------------------------------------------
// kb_sessions — new: "which sessions became knowledge articles?"
// ---------------------------------------------------------------------------

export const kbSessions: IntentDef = {
  id: 'kb_sessions',
  family: 'service',
  priority: 91,
  examples: ['Which sessions became knowledge articles?'],
  score: cues({
    any: [/knowledge articles?|\bkb articles?\b|became (knowledge|articles?)|knowledge base/],
    base: 6,
  }),
  async answer({ kit }) {
    kit.ran('knowledge_articles');
    const service = await loadOemDomain('service');
    const titled = service.kb.map((k) => {
      const title = /^# (.+)$/m.exec(k.text)?.[1] ?? k.id;
      const session = /Source session:\*\*\s*([A-Z]+-\d+)/.exec(k.text)?.[1];
      return { id: k.id, title: title.replace(/^KB-\d+\s*—\s*/, ''), session, file: k.file };
    });
    const withKb = service.xreach_sessions.filter((s) => s.knowledge_article);
    const markdown =
      `**${service.kb.length} knowledge articles** came out of remote-support sessions ` +
      `(${withKb.length} of ${service.xreach_sessions.length} sessions carry one):\n\n` +
      titled
        .slice()
        .reverse()
        .slice(0, 8)
        .map((k) => `- **${k.id}** ${k.title}${k.session ? ` — from ${k.session}` : ''}`)
        .join('\n') +
      `\n\nThe Knowledge agent attaches them to matching alerts; the newest is KB-0143 from the L-33 connector call at Mount Read.`;
    return kit.done(markdown, ['xreach_sessions.json', 'knowledge articles'], {
      navigate: route('remote'),
    });
  },
};

// ---------------------------------------------------------------------------
// contracts — new: "which contracts are up for renewal?"
// ---------------------------------------------------------------------------

export const contracts: IntentDef = {
  id: 'contracts',
  family: 'service',
  priority: 96,
  examples: ['Which contracts are up for renewal?', 'When does the Broken Ridge contract expire?'],
  score: cues({ any: [/contracts?\b/, /renewal|renew\b|renews/], base: 5 }),
  async answer({ kit, E }) {
    kit.ran('contract_renewals');
    const service = await loadOemDomain('service');
    const list = renewals({ contracts: service.mx_contracts }).filter(
      (c) => !E.mine || c.customer.toLowerCase().startsWith(MINES[E.mine].name.split(' ')[0].toLowerCase()),
    );
    const soon = list.filter((c) => c.days <= 365);
    const markdown =
      `**${soon.length} of ${list.length} site contracts renew inside a year**${E.mine ? ` (${MINES[E.mine].name})` : ''}.\n\n` +
      `| Customer | Contract | Type | Ends | Days | Machines | Labour A$/h |\n|---|---|---|---|---|---|---|\n` +
      list
        .map(
          (c) =>
            `| ${c.customer}${c.contractor ? ` (${c.contractor})` : ''} | ${c.contractnum} | ${c.type} | ${c.end} | ${c.days} | ${c.machines} | ${c.labour_rate_aud_h} |`,
        )
        .join('\n') +
      (list[0] ? `\n\nNext up: **${list[0].customer}** in ${list[0].days} days — the Commercial agent flags upgrade candidates before it.` : '');
    return kit.done(markdown, ['mx_contracts', 'Maximo service register'], { navigate: route('loops', 'commercial') });
  },
};
